/**
 * Alert sounds while the app is open: a ring for incoming calls and a short chime for
 * messages and mentions. Tones are synthesized (no audio files). Pushes to a closed app
 * use the phone's own notification sound instead.
 *
 * Browsers (iOS in particular) only allow audio after the user has interacted with the
 * page, so one shared AudioContext is resumed on the first tap or key press.
 */
import { useStore, wantsNotification, type State } from '../store';
import { appInBackground } from './native';

/* -------------------------------------------------------------- settings */

export interface SoundSettings {
  calls: boolean;
  messages: boolean;
  /** 0..1 */
  volume: number;
}

const KEY = 'sound-settings';
const DEFAULTS: SoundSettings = { calls: true, messages: true, volume: 0.7 };

export function getSoundSettings(): SoundSettings {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY) ?? '{}') as object) };
  } catch {
    return DEFAULTS;
  }
}

export function setSoundSettings(patch: Partial<SoundSettings>) {
  const next = { ...getSoundSettings(), ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode: keep the defaults */
  }
  return next;
}

/* ----------------------------------------------------------------- audio */

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  ctx ??= new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
  return ctx;
}

/** The app's AudioContext (resumed on the first interaction), shared with calls. */
export const sharedAudioContext = audio;

/** Unlock audio on the first interaction, so later alerts can play. */
export function installAudioUnlock() {
  if (typeof window === 'undefined') return;
  const unlock = () => {
    audio();
    if (ctx?.state === 'running') {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    }
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}

/** One tone with a soft attack and release (no clicks). */
function tone(c: AudioContext, freq: number, start: number, length: number, peak: number) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peak, start + 0.02);
  gain.gain.setValueAtTime(peak, start + length - 0.05);
  gain.gain.linearRampToValueAtTime(0, start + length);
  osc.connect(gain).connect(c.destination);
  osc.start(start);
  osc.stop(start + length);
}

/** The classic two-tone ring (440 + 480 Hz) for 1.2 s. */
function ring(volume: number) {
  const c = audio();
  if (!c) return;
  const t = c.currentTime;
  tone(c, 440, t, 1.2, 0.18 * volume);
  tone(c, 480, t, 1.2, 0.18 * volume);
}

/** A short rising two-note chime. */
export function ping(volume = getSoundSettings().volume) {
  const c = audio();
  if (!c) return;
  const t = c.currentTime;
  tone(c, 880, t, 0.12, 0.25 * volume);
  tone(c, 1320, t + 0.11, 0.16, 0.25 * volume);
}

/* ------------------------------------------------------------- ringing */

let ringTimer: number | null = null;

/** Ring every 3 s until stopped (incoming calls). */
export function startRinging() {
  if (ringTimer !== null) return;
  const once = () => {
    const s = getSoundSettings();
    if (s.calls && !onDnd(useStore.getState()) && !appInBackground()) ring(s.volume);
  };
  once();
  ringTimer = window.setInterval(once, 3000);
}

export function stopRinging() {
  if (ringTimer !== null) window.clearInterval(ringTimer);
  ringTimer = null;
}

/* ------------------------------------------------------------- messages */

const MIN_GAP_MS = 1500;
let lastPingAt = 0;

function onDnd(s: State) {
  const me = s.me?.id;
  return !!me && s.presences[me]?.status === 'dnd';
}

/** The conversation on screen, from the URL (/channels/<server or @me>/<channel>). */
function activeChannelId(): string | null {
  return /^\/channels\/[^/]+\/(\d+)/.exec(window.location.pathname)?.[1] ?? null;
}

/**
 * Whether a new message should chime: same rules as notifications (level, mutes, threads),
 * never your own, not on Do Not Disturb, not for the conversation you're looking at (unless
 * the app is in the background), and at most once every 1.5 s.
 */
export function shouldPing(
  s: State,
  m: { channelId: string; authorId: string; mentioned: boolean },
  ctx: { activeChannelId: string | null; hidden: boolean; lastPingAt: number; now: number },
): boolean {
  if (m.authorId === s.me?.id || onDnd(s)) return false;
  if (!ctx.hidden && ctx.activeChannelId === m.channelId) return false;
  if (ctx.now - ctx.lastPingAt < MIN_GAP_MS) return false;
  return wantsNotification(s, m.channelId, m.mentioned);
}

/** Chime for a new message if the rules and the user's settings allow it. */
export function messageSound(m: { channelId: string; authorId: string; mentioned: boolean }) {
  const settings = getSoundSettings();
  if (!settings.messages || appInBackground()) return;
  const now = Date.now();
  const ok = shouldPing(useStore.getState(), m, {
    activeChannelId: activeChannelId(),
    hidden: document.hidden,
    lastPingAt,
    now,
  });
  if (!ok) return;
  lastPingAt = now;
  ping(settings.volume);
}
